/**
 * Variant Upgrade Context:
 * ========================
 *
 * When the product data structure was upgraded from all SKUs being their own products,
 * to products with children using variants, code within this file was refactored.
 * The data structure in the `quiz-data.liquid` file was structure to replace the
 * product object with a variants option. To avoid unplanned consequences, the least
 * amount of changes occurred in the logic. This means that some variables labelled
 * `product(s)` is actually variant data such as `this.products = data.variants`.
 *
 * Outside of this, several other formatting updates were completed for code quality.
 */

class QuizForm extends HTMLElement {

  constructor() {
    super();

    this.logLevel = this.getAttribute('init-log-level');
    this.whyNoResults = '.no-recommendations';
    this.appError = '';
    this.invalidInputMessage = 'Please enter a valid value';
    this.exitQuizEarly = false;

    // dom
    this.elements = {
      quizSlides: this.querySelectorAll('form.quiz__slide'),
      quizControls: this.querySelector('.quiz__controls'),
      nextBtn: this.querySelector('#nextBtn'),
      prevBtn: this.querySelector('#prevBtn'),
      navControls: this.querySelectorAll('.js-quiz-nav'),
      toggleButton: this.querySelector('.js-quiz__accordion-trigger'),
      takeQuizForAnotherPetButton: this.querySelector('.quiz__retake-button'),
      noneRadios: this.querySelectorAll('.js-none-radio'),
      noneOptionSlides: this.querySelectorAll('.js-quiz__slide-noneOption'),
      emailInput: this.querySelector('#email'),
      subscribeInput: this.querySelector('#subscribe'),
      petNameInput: this.querySelector('#petName'),
      birthdaySlide: this.querySelector('[data-slide="petAge"]'),
      savedPetNameInputs: [],
      addBundleToCart: this.querySelectorAll('.button.button--primary.button--regular.atc'),
      closeModal: this.querySelector('.quiz__modal-close'),
      quizModalWrapper: this.querySelector('.quiz__modal'),
      progressWrapper: this.querySelector('.quiz__progress-wrapper'),
      progressBar: this.querySelector('.js-progress-bar'),
      petNameSlide: this.querySelector('[data-slide="petName"]'),
      nameSpans: this.querySelectorAll('.quiz__modal-title span, .quiz__question-title span, .quiz__question-helper span, .quiz__results-title span, .quiz__results-lead-text span'),
      quizBundle: this.querySelector('.quiz__bundle'),
      quizResults: this.querySelector('.quiz__results'),
      quizNoResults: this.querySelector('.quiz__results.no-results'),
      quizNoResultsReasons: this.querySelectorAll('.quiz__no-results-continue.quiz__no-results-tile'),
      wellnessSelects: this.querySelectorAll('.quiz__question-numberedSelect select'),
      wellnessGoalsResultsList: this.querySelector('ul.quiz__results-product-attributes-list'),
      bundleFrequencySelect: this.querySelector('#bundleFrequency'),
      quizPaymentTabs: this.querySelectorAll('.quiz__tab'),
      subscriptionPayment: this.querySelector('.quiz__payment-options.subscription'),
      oneTimeBuyPayment: this.querySelector('.quiz__payment-options.one-time-buy'),
    };

    // navigation
    this.currentTab = 0;
    this.currentForm = this.querySelectorAll('form.quiz__slide')[this.currentTab];
    this.currentSlideName = this.currentForm.getAttribute('data-slide');

    // bind this to functions
    this.getProducts = this.getProducts.bind(this);
    this.initProducts = this.initProducts.bind(this);
    this.validateQuizFilters = this.validateQuizFilters.bind(this);
    this.showTab = this.showTab.bind(this);
    this.nextPrev = this.nextPrev.bind(this);
    this.takeQuizForAnotherPet = this.takeQuizForAnotherPet.bind(this);
    this.handlePetSelect = this.handlePetSelect.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.emailIsValid = this.emailIsValid.bind(this);
    this.checkForExistingCustomer = this.checkForExistingCustomer.bind(this);
    this.getCustomer = this.getCustomer.bind(this);
    this.getCustomerMetafields = this.getCustomerMetafields.bind(this);
    this.upsertCustomer = this.upsertCustomer.bind(this);
    this.upsertMetafield = this.upsertMetafield.bind(this);
    this.upsertCurrentMetafield = this.upsertCurrentMetafield.bind(this);
    this.calculateAge = this.calculateAge.bind(this);
    this.filterProducts = this.filterProducts.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.updateLineItems = this.updateLineItems.bind(this);
    this.setLineItemQuantity = this.setLineItemQuantity.bind(this);
    this.handleResults = this.handleResults.bind(this);
    this.openProductModal = this.openProductModal.bind(this);
    this.closeProductModal = this.closeProductModal.bind(this);
    this.toggleModalAccordion = this.toggleModalAccordion.bind(this);
    // this.productModaltrapFocus = this.productModaltrapFocus(this);

    // requests to middleware api via 'Metaquiz' custom app (proxy)
    this.proxyUrl =  this.getAttribute('init-metaquiz-endpoint');
    this.makeHttpRequest = this.makeHttpRequest.bind(this);

    // helpers
    this.debug = this.debug.bind(this);

    // user management
    this.customer = {};
    this.userIsLoggedIn = this.getAttribute('init-user-is-logged-in') === 'true'; // this is set on quiz-form in page.quiz.liquid

    // pet management
    this.petName = 'your Pup';
    this.savedPetNames = [];
    this.wellnessGoals = [];

    // data
    this.quizDataURL = `${this.getAttribute('init-quiz-data-url')}?section_id=quiz-data`;
    this.quizVersion = this.getAttribute('init-quiz-version');
    this.localStorageKey = `greenies_quiz_${this.quizVersion}`;
    this.metafieldKeySeparator = '|';
    this.localQuizzes = {};
    this.customerMetafields = [];
    this.upsertMetafieldPostData = {};
    this.ignoreInputs = ['oseid'];
    this.setInputOnThis = ['petName'];
    this.metafield = {
      id: '',
      namespace: this.localStorageKey,
      key: '',
      description: 'Instance of quiz',
      value: {
        answers: {},
        completed: false,
        updated_at: '',
        products_recommended: [],
        added_to_cart: [],
        // products_ordered: [],
      },
    };
    this.answers = {};
    this.answer = {
      answer: '',
      timestamp: '',
    };
    this.additionalData = this.getAttribute('init-additional-data');
    this.products = {};
    this.recommendedProducts = [];

    // maps
    this.lifeStageMap = this.getAttribute('init-life-stage-map').replace(/\s/g,'');

    // filtering
    this.exclusionFilters = this.getAttribute('init-exclusion-filters');
    this.inclusionFilters = this.getAttribute('init-inclusion-filters');
    this.groupResultsBy = this.getAttribute('init-group-results-by');
    this.groupRanking = this.getAttribute('init-group-ranking');
    this.orderResultsBy = this.getAttribute('init-order-results-by');

    // preferences
    this.expirationDays = this.getAttribute('init-expiration-days');

    // other static settings
    this.resultsCtaText = this.getAttribute('init-results-cta-text');
    this.resultsPdpLink = this.getAttribute('init-results-pdp-link');

    // are we in the theme editor?
    this.showAllSlides = this.getAttribute('init-show-all-slides');

    // When in the editor and a quiz section reloads, stop the slide from being hidden
    document.addEventListener('shopify:section:load', function(event) {
      const slides = event.target.querySelector('form.quiz__slide');
      const results = event.target.querySelector('.quiz__results');
      slides ? slides.style.display = 'block' : results.style.display = 'block';
      if (results) {
        const noResultsBlocks = event.target.querySelectorAll('.quiz__no-results-continue');
        noResultsBlocks.forEach(block => block.style.display = "block");
      };
    });


    // navigation events
    Array.from(this.elements.navControls).forEach(element => element.addEventListener('click', this.nextPrev));
    // this.elements.takeQuizForAnotherPetButton.addEventListener('click', this.takeQuizForAnotherPet);
    Array.from(this.elements.quizSlides).forEach(element => element.addEventListener('submit', e => { this.elements.nextBtn.click(); e.preventDefault(); }));

    // form behavior events
    Array.from(this.elements.noneOptionSlides).forEach(element => element.addEventListener('change', this.toggleNone));
    Array.from(this.elements.noneRadios).forEach(element => element.addEventListener('click', this.noneReset));
    this.elements.toggleButton ? this.elements.toggleButton.addEventListener('click', this.toggleAccordion) : null;
    Array.from(this.elements.wellnessSelects).forEach(element => element.addEventListener('change', this.disableSelectOption));

    // bundle and add to cart events
    this.elements.quizBundle.addEventListener('change', this.updateLineItems);
    this.elements.bundleFrequencySelect.addEventListener('change', this.setLineItemQuantity);
    this.elements.addBundleToCart.forEach(element => element.addEventListener('click', () => this.addItemsToCart(element, this.lineItems)));
    this.elements.closeModal.addEventListener('click', this.closeModal);
    this.elements.quizModalWrapper.addEventListener('click', this.closeModal);
    this.elements.quizPaymentTabs.forEach(element => element.addEventListener('click', () => this.handlePurchaseTypes(element)));

    // action events
    this.elements.birthdaySlide ? this.elements.birthdaySlide.addEventListener('change', this.calcLifeStage) : null;
    this.elements.petNameInput.addEventListener('focus', this.handlePetSelect);
  }

  /* init */
  connectedCallback() {
    this.debug(`Log level: ${this.logLevel}`);
    this.debug(`Quiz version: '${this.quizVersion}'`);
    this.debug(`The quiz contains ${this.elements.quizSlides.length} questions`);
    this.debug(`User is a logged in customer? ${this.userIsLoggedIn}`);
    this.showTab(this.currentTab);
    this.initUser();
    this.getLocalQuizzes();
    this.initSettingsData();

    this.debug('Quiz Data URL', true)
    this.debug(this.quizDataURL)
  }

  async initUser(){
    if(this.userIsLoggedIn){
      this.debug('Initializing logged in user...');
      this.customer = {
        id: `gid://shopify/Customer/${this.getAttribute('init-customer-id')}`,
        email: this.getAttribute('init-customer-email'),
        acceptsMarketing: this.getAttribute('init-customer-accepts-marketing') === 'true',
      }
      // populate the email input with the logged in customer's email
      this.elements.emailInput.value = this.customer.email;
      if(this.currentSlideName === 'email'){
        if(this.customer.acceptsMarketing){
          this.debug('Hiding subscribe checkbox because the customer is logged in and already accepts marketing');
          this.elements.subscribeInput.parentElement.classList.add('hidden');
        } else {
          this.elements.subscribeInput.parentElement.classList.remove('hidden');
        }
      }
      this.debug('Checking for existing metafields on logged in customer');
      const customerMetafields = await this.getCustomerMetafields();
      if(customerMetafields){
        this.customerMetafields = customerMetafields;
      }
      this.debug('...logged in user initialized');
    }
  }

  async getProducts(){

    this.debug('Getting products...');

    try {
      const url = this.quizDataURL;
      let r = await fetch(url);
      let txt = await r.text();
      this.debug('Response txt');
      this.debug(txt);
      if(r.status !== 200){
        this.appError = 'There was an error. Please try again later.';
        this.debug(`response status: ${r.status}`);
        return null;
      } else if (!txt.length) {
        this.appError = 'No products were found. Please try again later.';
        return null;
      } else {
        this.debug(`...success from GET ${url}`);
        const tempElm = document.createElement('div');
        tempElm.innerHTML = txt;
        const productJSONscript = tempElm.querySelector('script');
        const productJSON = productJSONscript.textContent;
        this.debug('productJSON')
        this.debug(productJSON)
        const data = JSON.parse(productJSON);
        this.products = data.variants;
        this.debug('Products:');
        this.debug(this.products);
        return true;
      }
    } catch (e){
      this.appError = 'There was an error. Please try again later.';
      console.error(e);
      return null;
    }
  }

  async initProducts(){
    try {
      await this.getProducts();
      this.validateQuizFilters();
    } catch(error) {
      console.error(error);
    }
  }

  validateQuizFilters(){
    let shopUrl = `https://${window.Shopify.shop}/admin/products/`;
    let options = {};
    let optionsFrequencies = {};
    const ignore = [];
    let productsWithMetafieldErrors = {};
    for(let p of this.products){
      if(ignore.includes(p.id)){
        continue;
      }
      const f = p.quiz_filters;
      for(const [key, value] of Object.entries(f)){
        if(!Array.isArray(options[key])){
          options[key] = [];
        }
        if(!Array.isArray(optionsFrequencies[key])){
          optionsFrequencies[key] = [];
        }
        let msgs = [];
        if(value === "placeholder"){
          msgs.push(`'${key}' contains 'placeholder'`);
        }
        if(value === ""){
          msgs.push(`'${key}' is empty`);
        }
        if(msgs.length){
          const errKey = shopUrl + p.id;
          if(!Array.isArray(productsWithMetafieldErrors[errKey])){
            productsWithMetafieldErrors[errKey] = [];
          }
          productsWithMetafieldErrors[errKey] = productsWithMetafieldErrors[errKey].concat(msgs);
        }
        if(!options[key].includes(value)){
          options[key].push(value);
        }
        for(let [k, freqObjects] of Object.entries(optionsFrequencies)){
          if(k === key){
            const indexOfValueObj = freqObjects.findIndex(o => o.option_value === value);
            if(indexOfValueObj === -1){
              optionsFrequencies[key].push({ option_value: value, count: 1, product_id: [shopUrl + p.id] });
            } else {
              freqObjects[indexOfValueObj].count++;
              freqObjects[indexOfValueObj].product_id.push(shopUrl + p.id);
            }
          }
        }
      }
    }
    this.debug(`Options grouped by quiz filter metafield`);
    this.debug(options);
    this.debug(`Options grouped by quiz filter metafield with frequencies`);
    this.debug(optionsFrequencies);
    this.debug(`Errors on quiz filter metafields grouped by product ID`);
    this.debug(productsWithMetafieldErrors);
  }

  initSettingsData(){
    try {
      this.additionalData = this.parseKeyValueSettings(this.additionalData);
      this.exclusionFilters = this.parseCommaDelimitedSettings(this.exclusionFilters);
      this.inclusionFilters = this.parseCommaDelimitedSettings(this.inclusionFilters);
      this.groupRanking = this.parseCommaDelimitedSettings(this.groupRanking);
      this.orderResultsBy = this.parseKeyValueSettings(this.orderResultsBy);
    } catch(error) {
      console.error(error);
    }
  }

  /* bundles/subscriptions - recommendations - add to cart */

  handleResults(){
    this.variantArray = [];

    // Determine subscription products
    this.recommendedProducts.forEach((variant) => {
      if (variant.selling_plan_groups[0]) {
        if(Number.isInteger(parseInt(variant.quiz_filters.units_per_60_days, 10))){
          let unitsPer60Days = parseInt(variant.quiz_filters.units_per_60_days);
          unitsPer60Days = unitsPer60Days > 0 ? unitsPer60Days : null;
          variant._units_per_60_days = unitsPer60Days;
        }
        variant.product_main_attribute = variant.quiz_filters.product_main_attribute;
        this.variantArray.push(variant);
      } else {
        this.debug('Variant has no selling plan groups:');
        this.debug(variant);
      }
    });

    this.debug('Recommended variants array');
    this.debug(this.variantArray);

    this.lineItems = { items: [] };

    this.bundleResults = this.variantArray.map((variant) => {

      const dataAttributes = variant.selling_plan_groups[0].selling_plans.map(plan => {
        return `data-${((plan.name.split(".")[0]).split("every")[1]).trim().replace(/\s+/g, '-').toLowerCase()}=${plan.id}`;
      });

      this.lineItems.items.push({
        id: variant.id,
        quantity: 1,
        selling_plan: variant.selling_plan_groups[0].selling_plans[0].id
      });

      let productTitleLink = `<div class="quiz__bundle-item-desc">${variant.product_title}</div>`
      this.debug('link value: ', this.resultsPdpLink)
      if (this.resultsPdpLink == "show_modal") {
        productTitleLink = `
          <div class="quiz__bundle-item-desc">${variant.product_title}<br/>
            <button class="quiz__bundle-item-desc modal-toggle" id="modalToggle" data-variant-id="${variant.id}">Product Details</button>
          </div>
        `
      } else if (this.resultsPdpLink == "link") {
        productTitleLink = `<a href="${variant.url}" target="_blank" class="quiz__bundle-item-desc">${variant.product_title}</a>`
      }

      return `
      <li class="quiz__bundle-item" data-product="${variant.id}" data-_units_per_60_days="${variant._units_per_60_days}" data-subscription-price="${variant.selling_plan_price}" data-price="${variant.price}" ${dataAttributes.join(' ')}>
        <div class="quiz__bundle-item-wrapper">
          <div class="quiz__bundle-item-qty">
            <span class="select select--small">
              <select name="qty" id="qty">
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
              </select>
            </span>
          </div>
          <div class="quiz__bundle-item-image">
            <img class="cart-item__image"
              src="${variant.image}"
              alt="test"
              loading="lazy"
              width="100"
              height="auto"
            >
          </div>
          ${ productTitleLink }

          <div class="quiz__popup quiz__popup--hidden" data-variant-name="${variant.id}" aria-hidden="true">
            <span class="quiz__popup-background popup-close"></span>
            <div class="quiz__popup-content">
            <button class="quiz__popup-close popup-close" aria-label="Close product details popup">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
              </svg>
            </button>
            <div class="quiz__popup-header">
              <img class="quiz__popup-image"
                src="${variant.image}"
                alt="product image for ${variant.product_title}"
                loading="lazy"
                width="auto"
                height="300"
              >
              <div class="quiz__popup-info-wrapper">
                <h3 class="quiz__popup-title">${variant.product_title}</h3>
              </div>
            </div>
            <div class="quiz__popup-info">
              <ul aria-label="Accordion Control Group Buttons" class="quiz__accordion-controls">
                <li class="${variant.metafields.description === null ? 'hidden' : 'quiz__accordion-wrapper'}">
                  <button type="button" class="quiz__popup-heading open" data-label="${variant.id}-1" aria-expanded="true">About</button>
                  <p class="quiz__popup-text open" data-header="${variant.id}-1" aria-hidden="false" tabindex="0">${variant.metafields.description}</p>
                </li>
                <li class="${variant.metafields.ingredients === null ? 'hidden' : 'quiz__accordion-wrapper'}">
                  <button type="button" class="quiz__popup-heading" data-label="${variant.id}-2" aria-expanded="false">Ingredients</button>
                  <p class="quiz__popup-text" data-header="${variant.id}-2" aria-hidden="true" tabindex="-1">${variant.metafields.ingredients}</p>
                </li>
                <li class="${variant.metafields.nutritional_info === null ? 'hidden' : 'quiz__accordion-wrapper'}">
                  <button type="button" class="quiz__popup-heading" data-label="${variant.id}-3" aria-expanded="false">Nutritional Information</button>
                  <p class="quiz__popup-text" data-header="${variant.id}-3" aria-hidden="true" tabindex="-1">${variant.metafields.nutritional_info}</p>
                </li>
                <li class="${variant.metafields.directions === null ? 'hidden' : 'quiz__accordion-wrapper'}">
                  <button type="button" class="quiz__popup-heading" data-label="${variant.id}-4" aria-expanded="false">Feeding Guidelines</button>
                  <p class="quiz__popup-text" data-header="${variant.id}-4" aria-hidden="true" tabindex="-1">${variant.metafields.directions}</p>
                </li>
              </ul>
            </div>
            </div>
          </div>

        </div>
      </li>
      `;

    }).join(' ');

    const bundles = this.querySelectorAll('.quiz__bundle-wrapper');
    bundles.forEach(bundle => bundle.innerHTML = this.bundleResults);
    this.populateProductAttributesInResults();
    this.setLineItemQuantity();
    this.updateLineItems();
    // set recommended quantity in units_per_<subscription frequency>_days metafield
    // this.setLineItemRecommendedQuantity();
    this.elements.quizNoResults.style.display = 'none';
    this.elements.quizResults.style.display = 'block';
    this.elements.quizBundle.style.display = 'block';

    // push to dataLayer on successful load of results
    this.updateDataLayerQuizStep(`${this.elements.quizSlides.length}-results`);

    const productModalOpen = document.querySelectorAll('.quiz__bundle-item-desc.modal-toggle');
    const productModalClose = document.querySelectorAll('.popup-close');
    const accordionToggle = document.querySelectorAll('.quiz__popup-heading');
    Array.from(productModalOpen).forEach(element => element.addEventListener('click', () => this.openProductModal(element)));
    Array.from(productModalClose).forEach(element => element.addEventListener('click', () => this.closeProductModal()));
    Array.from(accordionToggle).forEach(element => element.addEventListener('click', () => this.toggleModalAccordion(element)));
  }

  handlePurchaseTypes(e) {
    this.elements.quizPaymentTabs.forEach(element => element.classList.toggle('inactive'));
    if (e.getAttribute('data-option') == 'subscription') {
      this.elements.subscriptionPayment.style.display= 'block';
      this.elements.oneTimeBuyPayment.style.display = 'none';
    } else {
      this.elements.subscriptionPayment.style.display = 'none';
      this.elements.oneTimeBuyPayment.style.display = 'block';
    }
  }


  addItemsToCart(element, products) {
    const clonedProds = JSON.parse(JSON.stringify(products));
    clonedProds.items.forEach(product => { delete product['selling_plan'] });

    const myHeaders = new Headers();

    myHeaders.append('Content-Type', 'application/json');

    const raw = element.id == 'one-time-buy' ? JSON.stringify(clonedProds) : JSON.stringify(products);

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw,
        redirect: 'follow'
    };

    // TODO: handle error
    fetch('/cart/add.js', requestOptions)
        .then(response => {
          this.debug(response);
          if(response.ok){
            this.getLocalQuizzes();
            this.metafield = this.localQuizzes[this.petName];
            this.metafield.value.added_to_cart = { items: products.items, subscription_frequency: this.elements.bundleFrequencySelect.value };
            this.debug('This metafield after adding products added to cart');
            this.debug(this.metafield.value);
            this.save();

            const event = new CustomEvent('cartUpdated');
            document.dispatchEvent(event);
            document.querySelector('cart-items').dispatchEvent(event);
          }
          return response.text();
        })
        .then(result => {
          this.debug(result);
        })
        .catch(error => console.error(error));

    this.openModal();

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  populateWellnessGoalsInResultsFromSelections(){
    this.debug('Showing wellness goals on results slide');
    this.debug(this.wellnessGoals);

    this.elements.wellnessGoalsResultsList.querySelectorAll('.quiz__results-product-attributes-item').forEach(item => {
        item.style.display = 'none';
    });

    this.wellnessGoals.forEach((goal, index) => {
      const target = this.querySelector(`.quiz__results-product-attributes-item[data-title="${goal}"]`);

      if(target) {
        target.style.display = 'grid';
        target.style.order = index;
      }
    });
  }

  populateProductAttributesInResults(){
    this.debug('Showing wellness goals from recommended products on results slide');
    const wellnessGoalsOnRecommendedProducts = this.variantArray.map(v => v.product_main_attribute.split(', '));
    this.debug(wellnessGoalsOnRecommendedProducts);

    this.elements.wellnessGoalsResultsList.querySelectorAll('.quiz__results-product-attributes-item').forEach(item => {
        item.style.display = 'none';
    });

    wellnessGoalsOnRecommendedProducts.forEach((goal, index) => {
      for (var i = 0; i < goal.length; i++) {
        const target = this.querySelector(`.quiz__results-product-attributes-item[data-title="${goal[i]}"]`);

        if(target) {
          target.style.display = 'grid';
          target.style.order = index;
        }
      }

    });
  }

  setLineItemQuantity(){
    this.debug(`Setting line item quantities in bundle`);
    const lineItemElements = this.querySelectorAll('.quiz__bundle-item');
    this.debug('line item elements');
    this.debug(lineItemElements);
    lineItemElements.forEach(item => {
      const unitsPerMonth = 1;
      this.debug(`unitsPerMonth: ${unitsPerMonth}`);
      const qty = unitsPerMonth;
      const qtySelect = item.querySelector('select[name="qty"]');
      this.debug(`qty: ${qty}`);
      var options = qtySelect.options;
      for (let i = 0; i < options.length; i++){
        if(options[i].value == qty){
          this.debug(`Setting quantity to ${qty} on qty select`)
          this.debug(qtySelect);
          options[i].selected = true;
          break;
        }
      }
    });
  }

  setLineItemRecommendedQuantity(){
    this.debug(`Setting line item recommended quantities in bundle`);
    const bundleFrequency = parseInt(this.elements.bundleFrequencySelect.value.replace('-days', ''), 10) / 30;
    const lineItemElements = this.querySelectorAll('.quiz__bundle-item');
    this.debug('line item elements');
    this.debug(lineItemElements);
    lineItemElements.forEach(item => {
      const unitsPer60Days = item.dataset._units_per_60_days;
      this.debug(`unitsPer60Days: ${unitsPer60Days}`);
      if(null !== unitsPer60Days){
        const unitsPerMonth = unitsPer60Days / 2;
        const recommendedQty = Math.floor(unitsPerMonth * bundleFrequency);
        const qtySelect = item.querySelector('select[name="qty"]');
        this.debug(`recommendedQty: ${recommendedQty}`);
        var options = qtySelect.options;
        for (let i = 0; i < options.length; i++){
          if(options[i].value == recommendedQty){
            this.debug(`Setting quantity to ${recommendedQty} on qty select`)
            this.debug(qtySelect);
            options[i].selected = true;
            break;
          }
        }
      }
    });
  }

  updateLineItems() {
    this.debug('Updating line items...');

    const bundleFrequency = this.querySelector('#bundleFrequency').value;
    const lineItemElements = this.querySelectorAll('.quiz__bundle-item');

    let newSubscriptionPrice = 0;
    let newPrice = 0;
    let hasQty = false;

    const updatedItems = Array.from(lineItemElements).map((item) => {
      const sellingPlanId = item.getAttribute(`data-${bundleFrequency}`);
      const itemQuantity = item.querySelector('select').value;
      const itemId = item.getAttribute('data-product');
      const itemSubscriptionPrice = item.getAttribute('data-subscription-price');
      const itemPrice = item.getAttribute('data-price');

      newSubscriptionPrice += parseInt(itemSubscriptionPrice) * parseInt(itemQuantity);
      newPrice += itemPrice * itemQuantity;

      this.debug(`Item price: ${itemSubscriptionPrice}; Item quantity: ${itemQuantity}; New price: ${newSubscriptionPrice/100}`);
      this.debug(`Item price: ${itemPrice}; Item quantity: ${itemQuantity}; New price: ${newPrice}`);

      if (itemQuantity > 0) { hasQty = true };

      return {
        id: parseInt(itemId, 10),
        quantity: parseInt(itemQuantity, 10),
        selling_plan: parseInt(sellingPlanId, 10),
      }
    });

    this.debug(`Final new subscription price: ${newSubscriptionPrice}`);
    this.debug(`Final new price: ${newPrice}`);
    this.debug(`Final new subscription price toLocaleString: ${(newSubscriptionPrice/100).toLocaleString("en-US", {style:"currency", currency:"USD"})}`);
    this.debug(`Final new price toLocaleString: ${(newPrice).toLocaleString("en-US", {style:"currency", currency:"USD"})}`);

    this.lineItems.items = updatedItems;

    this.querySelector('.quiz__bundle-total.subscription').innerHTML = `${(newSubscriptionPrice/100).toLocaleString("en-US", {style:"currency", currency:"USD"})}`;
    this.querySelectorAll('.quiz__bundle-total.one-time-buy').forEach(price => price.innerHTML = `${(newPrice).toLocaleString("en-US", {style:"currency", currency:"USD"})}`);

    if (hasQty) {
      this.elements.addBundleToCart.forEach(button => button.disabled = false);
    } else {
      this.elements.addBundleToCart.forEach(button => button.disabled = true);
    }
  }


  /* navigation */

  takeQuizForAnotherPet(){
    this.debug('Resetting the quiz to take it for another pet');
    // TODO: fix
    // this.metafield.value.answers.email = this.createAnswer(this.customer.email);
    // this.elements.closeModal.click();
    // this.elements.quizControls.style.display = 'block';
    // this.elements.progressWrapper.style.display = 'block';
    // this.elements.quizResults.style.display = 'none';
    // this.elements.quizBundle.style.display = 'none';
    // this.elements.petNameInput.value = '';
    // this.showTab(1);
  }

  showTab(n) {
    // This function will display the specified tab of the form ...
    const isDesignMode = Shopify.designMode;
    if (isDesignMode && this.showAllSlides == 'true') {
      // If in the theme editor and show_all_slides is checked in theme settings, show all quiz slides at once for easy editing.
      this.allSlides = this.querySelectorAll('form.quiz__slide');
      this.allSlides.forEach(slide => slide.style.display = 'block');
      this.elements.quizNoResults.style.display = 'block';
      this.elements.quizResults.style.display = 'block';
      this.elements.quizNoResultsReasons.forEach((reason) => reason.style.display ='block');
    } else {
      this.currentForm = this.querySelectorAll('form.quiz__slide')[this.currentTab];
      this.currentSlideName = this.elements.quizSlides[this.currentTab].getAttribute('data-slide');
      this.debug(`Current tab: ${n}`, true);
      this.debug(`Current slide name: ${this.currentSlideName}`);
      const slides = this.elements.quizSlides;
      const progress = Math.ceil(((n + 1) / slides.length) * 100);
      this.querySelector('.quiz__progress-complete').className = `quiz__progress-complete quiz__progress-complete--${progress}`;
      this.elements.progressBar.style.width = `${progress}%`;

      // push to dataLayer on successful load of slide
      this.updateDataLayerQuizStep();

      slides[n].style.display = 'block'; // ... and fix the Previous/Next buttons:

      if (n === 0) {
          this.elements.prevBtn.style.display = 'none';
      } else {
          this.elements.prevBtn.style.display = 'flex';
      }

      if (n === 1){
        this.handleSavedQuizzes();
      }

      if (n === slides.length - 1) {
          this.elements.nextBtn.textContent = this.resultsCtaText;
      } else {
          this.elements.nextBtn.innerHTML = 'Next';
          this.elements.nextBtn.style.display = 'flex';
      }
    }
  }

  async nextPrev(event, goToSlide) {
    // DO NOT REMOVE
    this.getLocalQuizzes();
    this.debug(`JSON.parse(localStorage.getItem('greenies_quiz_uat')) at the top of nextPrev`);
    this.debug(JSON.parse(localStorage.getItem('greenies_quiz_uat')));
    this.debug('this.localQuizzes at the top of nextPrev');
    this.debug(this.localQuizzes);
    event.target.disabled = true;
    // This function will figure out which tab to display
    const n = parseInt(event.target.getAttribute('data-direction'), 0);
    const slides = this.elements.quizSlides;

    // if 'next' was clicked
    if(n === 1){
      // validate
      const formIsValid = await this.validateForm();
      this.debug(`Form is valid: ${formIsValid}`);
      if (!formIsValid){
        // TODO: descriptive error
        alert(this.appError || this.invalidInputMessage);
        event.target.disabled = false;
        return false;
      }
      // submit
      const formSubmitted = await this.handleFormSubmit();
      if(!formSubmitted){
        this.debug('Form submit error');
        event.target.disabled = false;
        return;
      } else {
        const isLastSlide = this.exitQuizEarly || this.currentTab === (this.elements.quizSlides.length - 1);
        if(isLastSlide){
          if(!this.exitQuizEarly){
            await this.initProducts();
            if(!this.products.length){
              this.debug('Could not fetch products');
              return;
            }
            this.filterProducts();
          }
          if(this.recommendedProducts.length){
            this.handleResults();
          } else {
            this.handleNoResults(this.whyNoResults);
          }
          this.handleQuizCompletion();
          return;
        }
      }
    } else {
      if(this.currentTab < 3){
        this.debug('Clicked prev before saving first answer');
        location.reload();
        return;
      }
    }
    // Hide the current tab:
    slides[this.currentTab].style.display = 'none';

    if(goToSlide) {
      this.currentTab = goToSlide;
    } else {
      // Increase or decrease the current tab by 1:
      this.currentTab = this.currentTab + n;
    }
    this.showTab(this.currentTab);
    event.target.disabled = false;
    return;
  }

  openProductModal(element) {
    const variantId = element.getAttribute('data-variant-id');
    const targetModal = this.querySelector(`[data-variant-name="${variantId}"]`);
    targetModal.classList.remove('quiz__popup--hidden');
    targetModal.setAttribute('aria-hidden', 'false');
    this.closest('body').classList.add('quiz-modal-open');

    this.productModaltrapFocus(targetModal);
  }

  productModaltrapFocus(targetModal) {
    const focusableElements = 'button, p:not([aria-hidden="true"]), [tabindex]:not([tabindex="-1"])';
    const firstFocusableElement = targetModal.querySelectorAll(focusableElements)[0];
    const focusableContent = targetModal.querySelectorAll(focusableElements);
    const lastFocusableElement = focusableContent[focusableContent.length - 1];

    document.addEventListener('keydown', function(e) {
      let isTabPressed = e.key === 'Tab' || e.keyCode === 9;
      if (!isTabPressed) {
        return;
      }
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    });
  }

  closeProductModal() {
    const targetModal = this.querySelector('.quiz__popup:not(.quiz__popup--hidden)');
    targetModal.classList.add('quiz__popup--hidden');
    targetModal.setAttribute('aria-hidden', 'true');
    this.closest('body').classList.remove('quiz-modal-open');
  }

  toggleModalAccordion(element) {
    const openAccordion = this.querySelector('.quiz__popup-heading.open');
    const openInfo = this.querySelector('.quiz__popup-text.open');

    if (openAccordion) {
      openAccordion.classList.remove('open');
      openInfo.classList.remove('open');
      openAccordion.setAttribute('aria-hidden', 'true');
      openInfo.setAttribute('aria-hidden', 'true');
      openInfo.setAttribute('tabindex', '-1');
    }

    const label = element.getAttribute('data-label');
    const targetInfo = this.querySelector(`[data-header="${label}"]`);

    if (element !== openAccordion) {
      element.classList.add('open');
      element.setAttribute('aria-expanded', 'true');
      targetInfo.classList.add('open');
      targetInfo.setAttribute('aria-hidden', 'false');
      targetInfo.setAttribute('tabindex', '0');
      targetInfo.focus();
    }
    const targetModal = this.querySelector('.quiz__popup:not(.quiz__popup--hidden)');
    this.productModaltrapFocus(targetModal);
  }

  openModal() {
    this.querySelector('.quiz__modal').classList.remove('quiz__modal--hidden');
    this.closest('body').classList.add('quiz-modal-open');
  }

  closeModal(e) {
    const quizModal = this.querySelector('.quiz__modal');
    const closeButton = this.querySelector('.quiz__modal-close');
    const closeButtonSvg = this.querySelector('.quiz__modal-close svg');

    if (e.target === quizModal || e.target === closeButton || e.target === closeButtonSvg) {
      quizModal.classList.add('quiz__modal--hidden');
      this.closest('body').classList.remove('quiz-modal-open');
    }
  }

  handleNoResults(showElementSelector = '.no-recommendations'){
    this.debug('this.whyNoResults');
    this.debug(this.whyNoResults);
    this.debug('Handling no results');
    this.debug('showElementSelector');
    this.debug(showElementSelector);
    this.elements.quizNoResults.querySelector(`${showElementSelector}`).style.display = 'block';
    this.elements.quizResults.style.display = 'none';
    this.elements.quizBundle.style.display = 'none'
    this.elements.quizNoResults.style.display = 'block';
  }

  /* form behavior - selects and checkboxes */
  toggleNone() {
    const noneRadio = this.querySelector('.js-none-radio');
    const checkBoxes = this.querySelectorAll('input[type="checkbox"]:checked').length;
    // eslint-disable-next-line consistent-return, array-callback-return
    const selects = [...this.querySelectorAll('select')].filter((sel) => {
      if (!sel.options[sel.selectedIndex].disabled) {
          return sel.options[sel.selectedIndex].value;
      }
    }).length;

    if (selects || checkBoxes) {
      noneRadio.checked = false;
    } else {
      noneRadio.click();
    }
  }

  noneReset() {
    const slideWrapper = this.closest('.quiz__slide');

    slideWrapper.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
      const thisCheckbox = checkbox;
      thisCheckbox.checked = false;
    });

    slideWrapper.querySelectorAll('select').forEach((selectElement) => {
      const thisSelect = selectElement;
      thisSelect.selectedIndex = 0;
    });
  }

  toggleAccordion() {
    const panel = this.nextElementSibling;
    this.classList.toggle('quiz__accordion-trigger--active');

    if (panel.style.maxHeight) {
        panel.style.maxHeight = null;
    } else {
        panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }

  disableSelectOption() {
    //disable wellness selected select options
    const selects = document.querySelectorAll('.quiz__question-numberedSelect select');
    const options = document.querySelectorAll('.quiz__question-numberedSelect select option');
    const concerns = document.querySelectorAll('input[name="otherConcerns"]');
    const selectedValues = Array.from(selects).map(select => select.value);

    options.forEach(option => {
      (selectedValues.includes(option.value) || option.value === 'Choose One')
        ? option.disabled = true
        : option.disabled = false;
    });

    concerns.forEach(concern => {
      (selectedValues.includes(concern.id))
        ? concern.disabled = true
        : concern.disabled = false;
    });
  }

  handlePetSelect(e){
    const savedPetNameInputs = this.elements.savedPetNameInputs;
    if(!savedPetNameInputs){
      return;
    }
    if(e.target.name === 'savedPetName'){
      this.elements.petNameInput.value = '';
    } else {
      this.elements.savedPetNameInputs.forEach((radioElement) => {
        const radio = radioElement;
        radio.checked = false;
      });
    }
  }

  async handleQuizCompletion(){
    this.debug('Handling quiz completion');
    this.elements.quizSlides[this.currentTab].style.display = 'none';
    this.elements.quizControls.style.display = 'none';
    this.elements.progressWrapper.style.display = 'none';
    this.elements.nextBtn.disabled = false;
    if(this.exitQuizEarly){
      this.metafield.value.completed = true;
      this.save();
    }
    // this.metafield = {};
  }

  async save(){
    this.debug('* Saving quiz *');
    await this.upsertCurrentMetafield();
    // for all users, save data to localStorage
    this.debug('local quizzes before saving local quiz');
    this.debug(this.localQuizzes);
    this.setLocalQuiz();
    this.debug('local quizzes after setting local quiz');
    this.debug(this.localQuizzes);
    this.getLocalQuizzes();
  }

  /* fetch */

  async makeHttpRequest(method, path, data){
    method = method.toUpperCase();
    let url = this.proxyUrl + path;
    let postData = {};

    if(method === 'GET'){
      url += data;
      postData = {
        method: method,
      };
    }
    if(method === 'POST'){
      let bodyData = JSON.stringify(data);
      postData = {
        method: method,
        body: bodyData,
      };
    }

    // Append shop as a param
    const shop = `shop=${window.Shopify.shop.split('.').shift()}`;
    if (url.includes('?')) url = `${url}&${shop}`;
    else url = `${url}?${shop}`;

    this.debug(postData);
    try {
      let response = await fetch(url, postData);
      this.debug('Response');
      this.debug(response);
      if(!response.ok){
        this.appError = 'There was an error. Please try again later.';
        // this.appError = response.status;
      } else {
        this.appError = '';
        const d = await response.json();
        if(null !== d.errors){
          // TODO: there was an error; display message?
          this.appError = 'There was an error. Please try again later.';
          this.debug(`...error from ${method} ${url}`);
          return null;
        } else if (null === d.data) {
          // TODO: there are no results; display message?
          this.debug(`...no results from ${method} ${url}`);
          return null;
        } else {
          Array.isArray(d.data) || typeof d.data === 'object';
          this.debug(`...success from ${method} ${url}`);
          return d.data;
        }
      }
    } catch (e){
      this.appError = 'There was an error. Please try again later.';
      // this.appError = e;
      console.error(e);
      return null;
    }
  }


  /* user management */

  async manageUser(){
    this.debug('Managing user...', true);
    this.debug(`JSON.parse(localStorage.getItem('greenies_quiz_uat'))`);
    this.debug(JSON.parse(localStorage.getItem('greenies_quiz_uat')));
    this.debug(`this.localQuizzes at the top of manageUser`);
    this.debug(this.localQuizzes);
    if(!this.userIsLoggedIn){
      const existingCustomer = await this.checkForExistingCustomer();
      if(existingCustomer){
        this.customer = existingCustomer;
      }
      let promptAccountCreate = true;
      if(this.customer.id){
        if(this.customer.state === 'ENABLED'){ // existing customer with an account
          promptAccountCreate = false;
          this.debug(`User has an enabled account but is not logged in`);
        } else {
          this.debug(`User has a customer record but does not have an enabled account`);
        }
      }
      if(promptAccountCreate){
        // prompt for account create
      }
    }
    this.debug('...user management complete');
  }

  async checkForExistingCustomer(){
    this.debug('Checking for existing customer');
    try{
      const inputEmail = this.elements.emailInput.value;
      this.debug(`Email entered: ${inputEmail}`);
      if(!inputEmail){
      	alert('Email is required');
        return;
      }
      return await this.getCustomer();
    } catch(err){
      console.error(err);
    }
  }

  // get customer with email
  async getCustomer(){
    this.debug(`Getting customer...`);
    const email = this.elements.emailInput.value || this.customer.email;
    try{
      const r = await this.makeHttpRequest('GET', '/customer', `?customerEmail=${email}`);
      this.debug(r);
      return r;
    } catch(err){
      console.error(err);
    }
  }

  // create or update a customer
  async upsertCustomer(id = '', email, acceptsMarketing){
    this.debug(`Upserting customer...`);
    const postData = {
      customerEmail: email || this.elements.emailInput.value || this.customer.email,
      customerAcceptsMarketing: acceptsMarketing,
    };
    if(id){
      postData.customerId = id;
    }
    this.debug('post Data:')
    this.debug(postData);
    try{
      const r = await this.makeHttpRequest('POST', '/customer', postData);
      this.debug(r);
      return r;
    } catch(err){
      console.error(err);
    }
  }

  async upsertSubscribedUser(){
    if(!this.customer.id){
      // create a customer record if no customer record exists for the email and the user is subscribed
      this.debug('The user does not have a customer record; creating one and subscribing the user');
      const newCustomer = await this.upsertCustomer('', this.elements.emailInput.value, true);
      let success = false;
      if(typeof newCustomer !== 'undefined' && null !== newCustomer){
        if(typeof newCustomer.id !== 'undefined'){
          this.customer = newCustomer;
          success = true;
          this.debug('New customer');
          this.debug(newCustomer);
        }
      }
      const optinAction = success ? 'success' : 'fail';
      this.updateDataLayerOptIn(this.elements.emailInput.value, optinAction);
    } else {
      // this is is a logged in customer who does not accept marketing in Shopify but has now subscribed through the form
      // update their record to reflect the change
      if(!this.customer.acceptsMarketing && this.elements.subscribeInput.checked === true){
        if(this.userIsLoggedIn){
          this.debug(`Logged in customer does not accept marketing in Shopify but has now subscribed through the form; updating customer record`);
          const updatedCustomer = await this.upsertCustomer(this.customer.id, this.customer.email, true);
          this.debug('updatedCustomer');
          this.debug(updatedCustomer);
        } else {
          this.debug('The user is not logged in; cannot update subscription status');
        }
      }
    }
  }

  /* metafields */
  // get metafields on customer with namespace (fetch quiz instances with active quiz_id for all pets)
  async getCustomerMetafields(){
    this.debug(`Getting customer metafields...`);
    try{
      const customerId = this.customer.id.split('/').pop();
      const r = await this.makeHttpRequest('GET', '/customer/metafields', `?customerId=${customerId}&metafieldNamespace=${this.metafield.namespace}`);
      if(r.length){
        this.debug(`...found ${r.length} customer metafields:`);
        this.debug(r);
      } else {
        this.debug(`...no customer metafields found`);
      }
      return r;
    } catch(err){
      console.error(err);
    }
  }

  // get metafield on customer with namespace and key (fetch quiz instance with active quiz_id for specified pet)
  async getCustomerMetafield(){
    this.debug(`Getting customer metafield...`);
    try{
      const customerId = this.customer.id.split('/').pop();
      const r = await this.makeHttpRequest('GET', '/customer/metafield', `?customerId=${customerId}&metafieldNamespace=${this.metafield.namespace}&metafieldKey=${this.metafield.key}`);
      this.debug(`...customer metafield:`);
      this.debug(r);
      return r;
    } catch(err){
      console.error(err);
    }
  }

  // get quiz from metafield
  async getQuiz(){
    this.debug(`Getting quiz...`);
    try{
      let r = await this.getCustomerMetafield();
      this.debug(`...quiz:`);
      this.debug(r);
      return r;
    } catch(err){
      console.error(err);
    }
  }

  // get quiz current answers
  async getQuizCurrentAnswers(){
    this.debug('Getting quiz current answers...');
    try{
      let r = this.getQuiz();
      let current_answers = extractCurrentAnswers(r);
      return current_answers;
    } catch(err){
      console.error(err);
    }
  }

  async upsertMetafield(){
    const postData = this.upsertMetafieldPostData;
    this.debug(`Upserting quiz metafield...`);
    try{
      const r = await this.makeHttpRequest('POST', '/customer/metafield', postData);
      return r;
    } catch(err){
      console.error(err);
      return false;
    }
  }

  async upsertCurrentMetafield(){
    this.debug('Upserting current metafield...');
    this.upsertMetafieldPostData = {
      customerId: this.customer.id,
      metafieldNamespace: this.metafield.namespace,
      metafieldKey: this.metafield.key,
      metafieldDescription: `Instance of quiz ${this.quizVersion}`,
      metafieldType: 'json',
      metafieldValue: JSON.stringify(this.metafield.value),
    };
    if(this.metafield.id){
      this.upsertMetafieldPostData.metafieldId = this.metafield.id;
    }
    const upsertedQuizMetafield = await this.upsertMetafield();
    if(!upsertedQuizMetafield){
      // TODO: handle error
      this.debug(upsertedQuizMetafield);
      return false;
    } else {
      this.metafield = upsertedQuizMetafield;
      this.metafield.value = JSON.parse(this.metafield.value);
      this.debug('this.metafield.value after upsert');
      this.debug(this.metafield.value);
      this.customerMetafields.push(upsertedQuizMetafield);
    }
  }


  /* local storage */

  // get quiz from local storage
  getLocalQuizzes(){
    this.debug(`Getting quiz(zes) from local storage with key '${this.localStorageKey}'`);
    const quizzesJSON = localStorage.getItem(this.localStorageKey);
    if(quizzesJSON){
      const localStorageQuizzes = JSON.parse(quizzesJSON);
      this.debug(`Found ${Object.keys(localStorageQuizzes).length} quizzes in local storage:`);
      this.debug(localStorageQuizzes);
      this.localQuizzes = localStorageQuizzes;
    } else {
      this.debug(`No quiz found in local storage with key '${this.localStorageKey}'`);
    }
  }

  // set quiz in local storage
  setLocalQuiz(){
    this.debug(`Setting quiz in local storage on key ${this.localStorageKey}`);
    this.localQuizzes[this.petName] = this.metafield;
    const quizzesJSON = JSON.stringify(this.localQuizzes);
    return localStorage.setItem(this.localStorageKey, quizzesJSON);
  }

  // remove quiz from local storage
  removeLocalQuiz(){
    this.debug(`Removing quiz from local storage with key ${this.localStorageKey}`);
    return localStorage.removeItem(this.localStorageKey);
  }


  /* helpers */

  generateMetafieldKey(){
    // metafield key = pet name
    if(!this.metafield.key){
      this.debug(`Metafield key not set; generating metafield key...`);
      if(!this.petName){
        // TODO: handle error
        alert('Pet name is required');
        return false;
      }
      const timestamp = Date.now();
      const separator = this.metafieldKeySeparator;
      this.petKey = this.petName.replaceAll(separator, '');
      const key = timestamp + separator + this.petKey;
      // metafield key max length = 30; truncate
      this.metafield.key = key.slice(0, 30);
      this.debug(`...generated metafield key ${this.metafield.key}`);
    }
    return true;
  }


  /* third party requests */

  updateDataLayerQuizStep(stepStr = ''){
    stepStr = stepStr || `${this.currentTab}-${this.currentSlideName}`
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'quiz',
      'action': `step-${stepStr}`
    });
  }

  updateDataLayerOptIn(email, action){
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'newsletter optin',
      'location': 'quiz',
      'email': email,
      'action': action
    });
  }


  /* dynamic select */

  handleSavedQuizzes(){
    const email = this.elements.emailInput.value || this.customer.email;
    this.debug(`Checking for quizzes in localStorage for user ${email}...`);
    if(!Object.keys(this.localQuizzes).length){
      this.debug('...no quizzes found in localStorage');
      return;
    }
    let petNames = [];
    for(let [key, metafield] of Object.entries(this.localQuizzes)){
      const currentAnswers = this.extractCurrentAnswers(metafield);
      if(email === currentAnswers.email){
        petNames.push(key);
      }
    }
    this.debug(`...found ${petNames.length} quiz(zes) in local storage for user ${email}`);
    if(petNames.length){
      this.debug(petNames);
    }
    this.savedPetNames = petNames;
    const container = this.currentForm.querySelector('.quiz__question.quiz__question-pill-grid');

    // only create the elements once
    if(petNames.length && container.childElementCount === 0){
      this.debug('Creating radio buttons for existing pet name(s)...');
      container.classList.remove('hidden');
      const petChoiceSubtitle = container.parentElement.querySelector('.quiz__question-subtitle');
      petChoiceSubtitle.classList.remove('hidden');
      for(let p of petNames){
        const petInput = document.createElement('input');
        petInput.type = 'radio';
        petInput.id = p;
        petInput.className = 'savedPetName';
        petInput.name = 'savedPetName';
        petInput.value = p;
        container.appendChild(petInput);
        const petLabel = document.createElement('label');
        petLabel.className = 'pill';
        container.appendChild(petLabel);
        const spanWrap = document.createElement('span');
        petLabel.appendChild(spanWrap);
        petLabel.htmlFor = p;
        const emptySpan = document.createElement('span');
        spanWrap.appendChild(emptySpan);
        const labelText = document.createTextNode(p);
        spanWrap.appendChild(labelText);
        this.elements.savedPetNameInputs.push(petInput);
        petInput.addEventListener('click', this.handlePetSelect);
      }
      this.debug(`...radio buttons for ${petNames.length} existing pet name(s) created`);
    }
  }


  /* form handling */

  async handleFormSubmit(){

    this.debug(`Handling quiz form '${this.currentSlideName}' submit`);

    this.debug(`this.localQuizzes at the top of handleFormSubmit`);
    this.debug(this.localQuizzes);

    // user subscribed on the form
    const subscribed = this.customer.acceptsMarketing || this.elements.subscribeInput.checked === true;
    this.debug(`User subscribed status: '${subscribed}'`);

    // update or create customer with acceptsMarketing property
    if(subscribed && this.currentSlideName === 'email'){
      await this.upsertSubscribedUser();
    }

    // collect user input, store on this.answers
    this.collectAnswers();

    this.debug(`this.localQuizzes in handleFormSubmit after regular inputs, before setting values when on petName slide`);
    this.debug(this.localQuizzes);

    // we're on petName slide; handle pet choice and quiz retrieval from localStorage
    if(this.currentSlideName === 'petName'){
      this.setPetAndQuiz();
    }

    /*
      if the user has subscribed AND there is no quiz in local storage (i.e., it was removed) AND there are customer metafields,
      search for metafield for this customer + pet; if it exists, update it;
    */
    if(subscribed && this.currentSlideName === 'petName' && (!this.localQuizzes[this.petName] || this.localQuizzes[this.petName].completed === false)){
      this.setLocalMetafieldToCurrentShopify();
    }

    // if there's no metafield key, this is a new metafield; generate a key
    if(this.currentSlideName === 'petName' && !this.metafield.key){
      this.generateMetafieldKey();
      this.setAdditionalData();
    }

    // if we're past the first 2 slides, we have data; save it
    if(this.currentSlideName !== 'email' && this.currentSlideName !== 'petName'){

      // set local metafield from user input
      this.updateLocalMetafieldFromAnswers();

      const isLastSlide = this.currentTab === (this.elements.quizSlides.length - 1);
      if(isLastSlide){
        this.metafield.value.completed = true;
      }

      await this.save();
      this.getLocalQuizzes();
      this.metafield = this.localQuizzes[this.petName];
      this.answers = {};
    }

    return true;
  }

  collectAnswers(){

    this.debug('Collecting answers from user input...');
    const form = this.elements.quizSlides[this.currentTab];
    const input = new FormData(form);
    this.debug('this.metafield before updating:');
    this.debug(this.metafield);
    this.debug(`this.localQuizzes in handleFormSubmit before regular inputs`);
    this.debug(this.localQuizzes);

    // regular inputs (single option)
    for (const [key, value] of input.entries()) {
      this.debug(`Setting key '${key}' to value '${value}'`);
      if(this.ignoreInputs.includes(key)){
        this.debug(`Skipping input '${key}' because it is in the array of keys to ignore`)
        continue;
      }
      if(this.setInputOnThis.includes(key)){
        this.debug(`Setting this.${key} to input '${value}' (because it is in the array of inputs to be set on this element's properties)`)
        this[key] = value;
      }
      let answer = this.createAnswer(value);
      // there can be only one current answer object for each question
      this.answers[key] = answer;
    }

    this.debug(`this.localQuizzes in handleFormSubmit before checkboxes`);
    this.debug(this.localQuizzes);

    // collect multiple selects as an array of answers
    if(this.currentSlideName === 'wellness_goal') {
      this.debug('wellness selects array');
      const answers = Array.from(this.elements.wellnessSelects).map(select => select.value).filter(value => value.length);
      let a = this.createAnswer();
      a.answer = answers;
      this.answers['wellness_goal'] = a;
    }

    // checkboxes (multiselect)
    const checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');
    // checkboxes should be collected as an array of answers on the answer property
    // if any checkbox inputs have the same name as a single value input, the checkbox value will overwrite the value set by the single value input above
    if(checkboxes.length){
      this.debug(`Found ${checkboxes.length} checkboxes`);
      let a = this.createAnswer();
      a.answer = [];
      for(let c of checkboxes){
        let key = c.name;
        this.debug(`Name of checkboxes is '${key}'`);
        a.answer.push(c.value);
        // set answers to answer object on every iteration; the last will contain the full collection in an array on the answer property
        // there can be only one current answer object for each question
        this.answers[c.name] = a;
      }
    }

    this.debug('...collected quiz answers:');
    this.debug(this.answers);
  }

  setPetAndQuiz(){
    /*
      radio buttons were created with name='savedPetName' from quizzes stored in localStorage, one for each pet
      a user selected a saved pet; we've got an email and at least one saved pet (existing quiz) in local storage
    */
    if(this.answers.savedPetName){
      const savedPetName = this.answers.savedPetName.answer;
      this.debug(`Found quiz for ${savedPetName} in local storage: '${savedPetName}'`);
      this.petName = savedPetName;
      this.answers['petName'] = this.answers.savedPetName;
      // if the user chooses an existing pet, if there is an unfinished quiz, prompt to continue editing
      this.debug(`JSON.parse(localStorage.getItem(this.localStorageKey))[this.petName].value`);
      this.debug(JSON.parse(localStorage.getItem(this.localStorageKey))[this.petName].value);

      const daysElapsedSinceUnfinishedQuiz = (Date.now() - this.localQuizzes[this.petName].value.updated_at) / (1000*60*60*24);
      this.debug(`Days elapsed since unfinished quiz: ${daysElapsedSinceUnfinishedQuiz}`);
      const expired = daysElapsedSinceUnfinishedQuiz > this.expirationDays;
      if(this.localQuizzes[this.petName].value.completed !== true && !expired){
        // if (window.confirm(`A quiz was started; continue editing existing quiz [OK] or discard and start a new one [Cancel]?`)) {
          this.debug(`There is an existing unfinished quiz in localStorage last updated within ${this.expirationDays} days; continuing that quiz...`);
          this.debug(`JSON.parse(localStorage.getItem(this.localStorageKey))[this.petName].value.answers`);
          this.debug(JSON.parse(localStorage.getItem(this.localStorageKey))[this.petName].value.answers);
          this.getLocalQuizzes();
          this.debug('this.metafield')
          this.debug(this.metafield)
          // set the local quiz to the currently selected pet's quiz
          this.metafield = this.localQuizzes[this.petName];
          this.debug('this.metafield')
          this.debug(this.metafield)

          const savedAnswersObjects = this.extractCurrentAnswers(Object.assign({}, this.metafield), true);
          this.debug('Saved answers objects');
          this.debug(savedAnswersObjects);
          this.answers = savedAnswersObjects;

          let savedAnswers = {};
          for(let [key, value] of Object.entries(savedAnswersObjects)){
            savedAnswers[key] = value.answer;
          }

          this.debug('Saved Answers', true);
          this.debug(savedAnswers);
          this.populateSavedAnswers(savedAnswers);

        // } else {
        //   this.debug(`User chose to discard the existing quiz for '${savedPetName}' and start a new one`);
        // }
      } else {
        this.debug(`Saved quiz for '${savedPetName}' was completed; starting a new one...`);
      }
    // no quiz found in local storage
    } else {
      this.debug(`No quiz found in local storage for this pet`);
    }

    // update pet name on all slides
    Array.from(this.elements.nameSpans).forEach(element => element.innerHTML = this.petName);
  }

  setLocalMetafieldToCurrentShopify(){
    // customer metafields will have been fetched if the customer is logged in
    // TODO: session management - check if customer is still logged in
    if(this.customerMetafields.length){
      this.debug(`There is no local quiz for ${this.petName}; checking for customer metafield that matches namespace '${this.metafield.namespace}' where the key contains '${this.metafieldKeySeparator + this.petName}'`);
      // get the most recent metafield for this pet
      const metafieldsForThisQuizAndPet = this.customerMetafields.filter(m => {
        return (
          m.completed !== true &&
          m.namespace === this.metafield.namespace &&
          m.key.indexOf(this.metafieldKeySeparator + this.petName) !== -1);
      });
      if(metafieldsForThisQuizAndPet.length){
        this.debug(`Found ${metafieldsForThisQuizAndPet.length} customer metafield(s) for namespace '${this.metafield.namespace}' where the key contains '${this.metafieldKeySeparator + this.petName}'`);
        let metafieldsForThisQuizAndPetSorted = metafieldsForThisQuizAndPet.sort((a, b) => {
          const aTimestamp = a.key.split(this.metafieldKeySeparator).shift();
          const bTimestamp = b.key.split(this.metafieldKeySeparator).shift();
          a.timestamp - b.timestamp
        });
        // set this metafield to the most recent customer metafield
        const mostRecentMetafield = metafieldsForThisQuizAndPetSorted.pop();
        this.metafield = mostRecentMetafield;
        this.metafield.value = JSON.parse(mostRecentMetafield.value);
        this.debug('Setting this.metafield.value to the most recently created metafield:');
        this.debug(this.metafield);
      } else {
        this.debug(`There is no matching customer metafield`);
      }
    }
  }

  updateLocalMetafieldFromAnswers(){
    // for both subscribers and non-subscribers, update this.metafield with current quiz answers
    this.debug('Updating this.metafield.value.answers with current answers...');
    this.debug('this.answers');
    this.debug(this.answers);
    this.debug('this.metafield.value.answers');
    this.debug(this.metafield.value.answers);
    for(let [key, value] of Object.entries(this.answers)){
      this.debug(`Updating ${key} on this.metafield.value.answers`);
      // skip reserved properties, which have the '_' prefix
      if(key[0] === '_'){
        continue;
      }
      if(typeof this.metafield.value.answers[key] === 'undefined'){
        this.debug(`'${key}' is undefined on this.metafield.value.answers; creating it...`);
        this.metafield.value.answers[key] = [];
      }
      this.metafield.value.answers[key].push(value);
    }

    const timestamp = Date.now();
    this.metafield.value.updated_at = timestamp;

    this.debug('this.metafield after update:');
    this.debug(this.metafield);
  }

  setAdditionalData(){
    this.debug('Setting static value(s) from settings.quiz_additional_data on answers');
    this.debug(this.additionalData);
    for(let [key, value] of Object.entries(this.additionalData)){
      const a = this.createAnswer(value);
      this.metafield.value.answers[key] = [a];
    }
  }


  /* validation */

  async validateForm(event) {
    this.debug('Local quizzes in validate form');
    this.debug(this.localQuizzes);
    // This function deals with validation of the form fields
    const slides = document.getElementsByClassName('quiz__slide');
    const slide = slides[this.currentTab].getAttribute('data-slide');
    const inputs = slides[this.currentTab].getElementsByTagName('input');
    let valid = true;

    if (slide === 'email') {
      this.debug('Validating email slide...');
      const emailValue = slides[this.currentTab].querySelector('input[name="email"]').value;
      if(!this.emailIsValid(emailValue)){
        valid = false;
      }
      else{
        const managedUser = await this.manageUser();
        if(this.appError){
          // TODO: should quiz continue if there's an app/network error?
          valid = false;
        }
      }
    } else if (slide === 'petName') {
      const petName = slides[this.currentTab].querySelector('input[name="petName"]').value;
      const savedPetNames = slides[this.currentTab].querySelectorAll('input[type="radio"]:checked').length;

      valid = !!((petName || savedPetNames));
    } else if (slide === 'petAge') {
      const petMonth = slides[this.currentTab].querySelector('#bdayMonth').value;
      const petYear = slides[this.currentTab].querySelector('#bdayYear').value;
      const lifeStage = slides[this.currentTab].querySelector('#life_stage');

      const d = new Date();
      const currentMonth = d.getMonth();
      const currentYear = d.getFullYear();
      let validDate = true;

      this.debug('currentMonth', currentMonth);
      this.debug('currentYear', currentYear);
      this.debug('petMonth', petMonth);
      this.debug('petYear', petYear);

      if(parseInt(petYear) === currentYear) {
        validDate = parseInt(petMonth) <= currentMonth;
      }

      valid = false;

      if (petMonth && petYear) {
        const monthsOld = this.calculateAge(petMonth, petYear);
        let petAgeString;
        let lifeSpanMapArray = this.lifeStageMap.split(/,(?=[^,]+:)/).map(s => s.split(':'));

        lifeSpanMapArray.forEach((item) => {
          const months = item[1].split('-');
          if (monthsOld >= months[0] && monthsOld <= months[1]) {
            petAgeString = item[0];
            if(petAgeString === 'puppyPlus'){
              petAgeString = 'puppy 6+ months';
            }
          }
        });

        this.debug('Life stage: ', petAgeString);

        lifeStage.value = petAgeString;
      }

      if (lifeStage.value && validDate) {
        if(lifeStage.value.toLowerCase() === 'puppy'){
          this.debug('No recommendations for puppy; exiting quiz');
          this.exitQuizEarly = true;
          this.whyNoResults = '.too-young';
          return true;
        }
        valid = true;
      }
    } else if (slide === 'weight') {
        const petWeight = slides[this.currentTab].querySelectorAll('input[type="radio"]:checked').length;
        valid = !!petWeight;
    } else if (slide === 'sensitivities') {
      const selectedSensitivities = slides[this.currentTab].querySelectorAll('input[type="checkbox"]:checked');
      for(let s of selectedSensitivities){
        if(s.value.toLowerCase() === 'chicken'){
          this.debug('No recommendations due to chicken sensitivity; exiting quiz');
          this.exitQuizEarly = true;
          this.whyNoResults = '.food-sensitivity';
          return true;
        }
      }
    } else if (slide === 'wellness_goal') {
      this.debug('Validating wellness goal slide');
      const selectedWellnessGoal = Array.from(this.elements.wellnessSelects).filter(select => select.options[select.selectedIndex].value.length);
      valid = selectedWellnessGoal.length;
    } else if (slide === 'food_types') {
      this.debug('Validating food types slide');
      const foodTypes = slides[this.currentTab].querySelectorAll('input[type="checkbox"]:checked');
      valid = foodTypes.length;
    } else if (slide === 'pet_habitat') {
      this.debug('Validating cat type slide');
      const petHabitat = slides[this.currentTab].querySelectorAll('input[type="radio"]:checked');
      valid = petHabitat.length;
    } else {
      // A loop that checks every input field in the current tab:
      for (let i = 0; i < inputs.length; i++) {
        // If a field is empty...
        if (inputs[i].value === '') {
          // add an "invalid" class to the field:
          inputs[i].className += ' invalid';
          // and set the current valid status to false:
          valid = false;
        }
      }
    }

    return valid; // return the valid status
  }

  emailIsValid(email){
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }


  /* helpers */

  // populateSavedAnswers if user chooses to continue saved quiz
  populateSavedAnswers(savedAnswers) {
    this.debug('Populating saved answers from local quiz answers');

    const forms = document.querySelectorAll('form.quiz__slide');
    const formsArray = Array.from(forms);
    let targetSlide = 0;

    for (const key in savedAnswers) {
      this.debug(`${key}: ${savedAnswers[key]}`);
      const target = document.querySelector(`[name="${key}"]`)?.closest('form');
      const indexTarget = formsArray.indexOf(target);
      if (indexTarget >= targetSlide) {
        targetSlide = indexTarget;
      }

      // skip reserved properties (which have the '_' prefix) and petName because petName should be selected automatically
      if (key === 'petName' || key[0] === '_') {
        continue;
      } else if (key === 'breed_size') {
          document.getElementById(`${savedAnswers[key]}`).checked = true;
      } else if (key === 'subscribe') {
          document.getElementById('subscribe').checked = true;
      } else if (key === 'wellness_goal') {
        savedAnswers[key].forEach((item, index) => {
            document.getElementById(`wellness_goal-${index + 1}`).value = item;
        });

        this.disableSelectOption();
      } else if (Array.isArray(savedAnswers[key])) {
        if (key === 'protein_src_main_ing') {
          document.getElementById('noSensitivities').checked = false;
        }
        savedAnswers[key].forEach((item) => {
            document.getElementById(item).checked = true;
        });
      } else {
        if (key === 'capsules' || key === 'tablets') {
            document.getElementById('medications').checked = false;
        }
        if(!Object.keys(this.additionalData).includes(key)){
          this.debug(`setting form input '${key}' to value '${savedAnswers[key]}'`)
          document.querySelector(`[name="${key}"]`).value = savedAnswers[key];
        }
      }
    }
    const slides = this.elements.quizSlides;
    slides[this.currentTab].style.display = 'none';
    this.currentTab = targetSlide; // Current tab is set to be the first tab (0)
    this.currentForm = this.querySelectorAll('form.quiz__slide')[this.currentTab];
    this.currentSlideName = this.currentForm.getAttribute('data-slide');
  }

  // extract the current answers from a quiz instance based on timestamp
  extractCurrentAnswers(quizMetafield = '', resultObjects = false){
    this.debug('Extracting current answers from quizMetafield:');
    quizMetafield = quizMetafield || this.metafield;
    this.debug(quizMetafield);
    const answers = Object.assign({}, quizMetafield.value.answers);
    let currentAnswers = {};
    for(const [key, value] of Object.entries(answers)){
      let skip = false;
      let skipMsg = '';
      if(!Array.isArray(value)){
        skipMsg = `its value is not an array`;
        skip = true;
      }
      if(Array.isArray(value)){
        if(!value.length){
          skipMsg = `its value is an empty array`;
          skip = true;
        }
      }
      if(skip){
        // this.debug(`Skipping ${key} because ${skipMsg}; each property must contain an an array of answers`);
        continue;
      }
      let sortedAnswers = value.sort((a, b) => {a.timestamp - b.timestamp});
      let answerObj = sortedAnswers.pop();
      if(resultObjects){
        currentAnswers[key] = answerObj;
      } else {
        currentAnswers[key] = answerObj.answer;
      }
    }
    this.debug('Current answers:');
    this.debug(currentAnswers);
    return currentAnswers;
  }

  // eslint-disable-next-line class-methods-use-this
  calculateAge(month, year) {
    const today = new Date();
    const m = today.getMonth() - month;

    let age = today.getFullYear() - year;

    age = age * 12 + m;
    return age;
  }

  createAnswer(value = ''){
    let a = Object.assign({}, this.answer);
    a.answer = value;
    a.timestamp = Date.now();
    return a;
  }

  debug(msg, newsection = false){
    if(this.logLevel === 'debug'){
      if(newsection){
        console.log(`*****************************************************************************`);
      }
      console.log('QUIZ:', msg);
    }
  }

  parseCommaDelimitedSettings(str){
    this.debug(`Parsing comma delimited settings: '${str}'`);
    let settings = [];
    if(str.length){
      const arr = str.split(',');
      for(let s of arr){
        const value = s.trim().toLowerCase();
        settings.push(value);
      }
    }
    this.debug(settings);
    return settings;
  }

  parseKeyValueSettings(str){
    this.debug(`Parsing comma delimited key-value settings: '${str}'`);
    let settings = {};
    if(str.length){
      const arr = str.split(',');
      for(let s of arr){
        const keyValuePair = s.split(':');
        const key = keyValuePair[0].trim();
        const value = keyValuePair[1].trim().toLowerCase();
        settings[key] = value;
      }
    }
    this.debug(settings);
    return settings;
  }


  /* filters - recommendations */

  async filterProducts() {
    let productsFiltered = [];

    this.debug(`Filtering products...`, true);

    const currentAnswers = this.extractCurrentAnswers(this.metafield);
    // const lifeStage = currentAnswers.life_stage.toLowerCase();

    this.debug('currentAnswers');
    this.debug(currentAnswers);

    // exclusion filters - exclude a product if its ingredients include an ingredient in (e.g., sensitivities) answer
    for(let exclusionFilter of this.exclusionFilters){

      productsFiltered = this.products.filter((product) => {

        if (!Object.keys(product.quiz_filters).length){
          this.debug(`No filters from metafields found on product ${product.product_id} '${product.title}'; skipping product`);
          return false;
        }

        this.debug(`Filters found on product ${product.product_id} '${product.title}'`);
        this.debug(product.quiz_filters);

        if(typeof product.quiz_filters[exclusionFilter] === 'undefined'){
          this.debug(`The metafield '${exclusionFilter}' does not exist on product ${product.product_id} '${product.title}'; skipping product`);
          return false;
        }

        const xStr = product.quiz_filters[exclusionFilter].toLowerCase();
        const xArr = xStr.split(',');
        const x = xArr.map((ingredient) => {
          return ingredient.trim().toLowerCase();
        });

        let exclude = false;
        this.debug('currentAnswers[exclusionFilter: ', currentAnswers[exclusionFilter]);
        if (currentAnswers[exclusionFilter] && currentAnswers[exclusionFilter] !== 'none') {
          // if the pet is sensitive to any of the ingredients on the product, exclude the product
          for (let exclusion of currentAnswers[exclusionFilter]){
            const xVal = exclusion.toLowerCase();
            if(x.includes(xVal)){
              this.debug(`Product ${product.product_id} '${product.title}' contains '${xVal}' in '${exclusionFilter}' metafield; excluding from recommendations`);
              exclude = true;
              break;
            }
          }
        }
        return exclude === false;
      });
    }

    this.debug('productsFiltered after exclusion filters');
    this.debug(productsFiltered);


    // matching answers - include a product if all inclusion filter properties have a matching answer value
    // this filter applies to answers which have a single value
    productsFiltered = productsFiltered.filter((product) => {
      let foundCount = 0;
      for(let inclusionFilter of this.inclusionFilters){
        if(typeof product.quiz_filters[inclusionFilter] === 'undefined'){
          this.debug(`The metafield '${inclusionFilter}' does not exist on product on product ${product.product_id} '${product.title}'; skipping product`);
          continue;
        }
        const toIncludeStr = product.quiz_filters[inclusionFilter];
        const toIncludeArr = toIncludeStr.split(',');
        const toInclude = toIncludeArr.map((f) => {
          return f.trim().toLowerCase();
        });

        if (currentAnswers[inclusionFilter] !== undefined) {
          const answer = currentAnswers[inclusionFilter].toLowerCase();
          if(toInclude.includes(answer)){
            this.debug(`Found answer '${answer}' on product ${product.product_id} '${product.title}' in ${inclusionFilter} metafield: '${toInclude.join(', ')}'`);
            foundCount++;
          }
        }
      }
      if(this.inclusionFilters.length === foundCount){
        this.debug(`All inclusion filters match on product ${product.product_id} '${product.title}'; including in recommendations`);
        return product;
      }
    });

    this.debug('productsFiltered after inclusion filters');
    this.debug(productsFiltered);


    // create object with arrays keyed on this.groupResultsBy property (defined in settings.quiz_group_results_by)
    let resultsGrouped = {};
    for(let product of productsFiltered){
      let productGroupVal = product.quiz_filters[this.groupResultsBy];
      if(typeof productGroupVal === 'undefined'){
        this.debug(`The metafield '${this.groupResultsBy}' does not exist on product ${product.product_id} '${product.title}'; skipping product`);
        continue;
      }
      if(!productGroupVal.length){
        this.debug(`The metafield '${this.groupResultsBy}' is empty on product ${product.product_id} '${product.title}'; skipping product`);
        continue;
      }
      productGroupVal = productGroupVal.toLowerCase();

      if(!this.groupRanking.includes(productGroupVal)){
        this.debug(`The value '${productGroupVal}' is not a valid '${this.groupResultsBy}'; skipping product ${product.product_id} '${product.title}'`);
        continue;
      }
      if(!Array.isArray(resultsGrouped[productGroupVal])){
        resultsGrouped[productGroupVal] = [];
      }
      resultsGrouped[productGroupVal].push(product);
    }

    this.debug('resultsGrouped');
    this.debug(resultsGrouped);


    // sort recommendations within each group by properties in settings
    // e.g., by wellness goals and inventory (in stock) level
    this.debug('Ordering results by: ');
    this.debug(this.orderResultsBy);
    for(let [group, products] of Object.entries(resultsGrouped)){
      let productsSorted = products.sort((a, b) => {
        let comparedArr = [];
        for(let [orderBy, sortType] of Object.entries(this.orderResultsBy)){
          const orderByArr = orderBy.split('.');
          const targetType = orderByArr[0].trim();
          const orderByProperty = orderByArr[1].trim();
          let aVal, bVal;
          this.debug(`Target type: ${targetType}`);
          this.debug(`orderBy Property: ${orderByProperty}`);
          if(targetType === 'product'){
            aVal = a[orderByProperty];
            bVal = b[orderByProperty];
          } else if(targetType === 'quiz_filters'){
            aVal = a.quiz_filters[orderByProperty];
            bVal = b.quiz_filters[orderByProperty];
          } else if(targetType === 'answer' && currentAnswers[orderByProperty]){
            const lengthOfPropArr = currentAnswers[orderByProperty].length;
            /*
              use case for targetType === 'answer': ranked answers - 3 wellness goals in an array
                there is one wellness goal on each product
                get the array index of the product's wellness goal in the ranked answers,
                  e.g., with answers in the order 'Fresh Breath', 'Clean Teeth', 'Joint Health'
                in the array of products
                get the index of product a's wellness goal 'Clean Teeth' in the answers (index 1)
                get the index of product b's wellness goal 'Fresh Breath' in the answers (index 0)
                the lower the number, the higher the rank
                compare index a to index b and sort according to sortType (asc or desc)
                product b is sorted before a because the sortType is asc, index of 'Fresh Breath' in answers is 0 and 'Clean Teeth' is 1
            */
            if(Array.isArray(currentAnswers[orderByProperty])){
              /*
                if the value of the orderByProperty on the product.quiz_filters is not among the ranked answers, set its value to 1 greater than the largest index in the array of answers
                so that it will be ranked lowest; e.g., all products where the wellness goal is not a wellness goal answer
              */
              aVal = currentAnswers[orderByProperty].indexOf(a.quiz_filters[orderByProperty]);
              if(aVal === -1){
                aVal = lengthOfPropArr;
              }
              bVal = currentAnswers[orderByProperty].indexOf(b.quiz_filters[orderByProperty]);
              if(bVal === -1){
                bVal = lengthOfPropArr;
              }
            } else {
              // make aVal and bVal equal
              aVal = 1;
              bVal = 1;
            }
          } else {
            console.error(`Error in settings.quiz_order_results_by; '${targetType}' is an invalid target type; valid values are 'answer' and 'product'`)
          }
          this.debug(`aVal: ${aVal}`);
          this.debug(`bVal: ${bVal}`);
          let compared = sortType === 'asc' ? aVal - bVal : bVal - aVal;
          comparedArr.push(compared);
        }

        this.debug('Array of compared values to use in sort:');
        this.debug(comparedArr);

        let sortReturn;
        for(let c of comparedArr){
          if(!c){
            continue;
          }
          sortReturn = c;
          break;
        }
        return sortReturn;
      });
      resultsGrouped[group] = productsSorted;
    }

    this.debug('resultsGrouped sorted');
    this.debug(resultsGrouped);


    // calorie allowance
    let finalProductRecommendations = {};
    for(let [productCategory, productsSorted] of Object.entries(resultsGrouped)){
      for(let product of productsSorted){
        if(!Array.isArray(finalProductRecommendations[productCategory])){
          finalProductRecommendations[productCategory] = [];
        }
        finalProductRecommendations[productCategory].push(product);
      }
    }

    this.debug('finalProductRecommendations after calorie allowances');
    this.debug(finalProductRecommendations);


    // medications
    if(!currentAnswers.tablets && !currentAnswers.capsules && !currentAnswers.smallTablet  || currentAnswers.medications === 'none'){
      if(typeof finalProductRecommendations['pill pockets'] !== 'undefined'){
        this.debug('Removing pill pockets recommendations because the pet is not taking any medications');
        delete finalProductRecommendations['pill pockets'];
      }
    }

    this.debug('finalProductRecommendations after excluding medications');
    this.debug(finalProductRecommendations);

    /*
      among the results, prioritize these special cases if they exist:
        if protein_src_main_ing answer includes wheat and wellness goal is food sensitivity
          recommend the product in that category
        else if life_stage answer is puppy 6+ months and product wellness_goal is puppy
          or
          life_stage answer is senior and product wellness_goal is aging care
          recommend the product in that category
        else
          do not prioritize the product
        (food sensitivity takes precedence over age)
    */
    for(let [category, products] of Object.entries(finalProductRecommendations)){
      this.debug('Prioritizing products based on cat habitat');
      const habitat = currentAnswers.pet_habitat
      this.debug(`Habitat:`);
      this.debug(habitat);
      if(products.length){
        for(let product of products){
          let prioritizedProduct = null;
          const productHabitat = product.quiz_filters.marketing_cat_habitat.toLowerCase();
          this.debug(`Product habitat: ${productHabitat}`);
          // prioritize based on indoor equals hairball control
          if (habitat === 'indoor' && productHabitat === 'indoor') {
            this.debug(`Cat is an indoor cat ////prioritizing indoor products`);
            prioritizedProduct = product;
            this.debug('product: ', product)
          }
          // if the category contains a product to prioritize, assign it to that category in the results; there need only be one product in the array
          if(prioritizedProduct){
            this.debug('prioritized product');
            this.debug(prioritizedProduct);
            finalProductRecommendations[category] = [prioritizedProduct];
            break;
          }
        }
      }
    }

    this.debug('Product recommendations after filtering:');
    this.debug(finalProductRecommendations);


    // top recommendation from each category
    let recommendedProductsIDs = [];
    let recommendedProducts = [];
    for(let [key, value] of Object.entries(finalProductRecommendations)){
      if(value.length){
        recommendedProducts.push(value[0]);
        recommendedProductsIDs.push({
          product_id: value[0].product_id,
          first_variant_id: value[0].id,
        });
      }
    }

    this.debug('Final recommended products from each category');
    this.debug(recommendedProducts);

    this.recommendedProducts = recommendedProducts;
    this.wellnessGoals = currentAnswers.wellness_goal;
    this.getLocalQuizzes();
    this.metafield = this.localQuizzes[this.petName];
    this.metafield.value.products_recommended = recommendedProductsIDs;
    this.debug('This metafield after adding products recommended');
    this.debug(this.metafield.value);
    await this.save();

    // this.wellnessGoals = currentAnswers.wellness_goal;

    return true;

  }
}

customElements.define('quiz-form', QuizForm);